import {
  Button,
  Dialog,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/lab';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import { bulkUpdateCarryForwardedLeaves } from 'src/services/CarryForwardedLeaveService';
import ConfirmationModal from '../common/confirmationModal/ConfirmationModal';
import { useAppSelector } from 'src/hooks/hooks';

interface BulkUpdateFormValue {
  open: boolean;
  toggle: () => void;
  selectedIndexes: any[];
  carryForwardedLeaveList: any[];
  setSnackBarOpen: (open: boolean) => void;
  snackBarMessage: (message: string) => void;
  snackbarSeverity: (severity: string) => void;
  isClickContinue: any;
}

const BulkUpdate = (props: BulkUpdateFormValue) => {
  const dispatch = useDispatch();
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const [selectedCarryForwardLeaves, setSelectedCarryForwardedLeaves] =
    useState<string>('');
  const [selectedExpiredDate, setSelectedExpireDate] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [openConformation, setOpenConformation] = useState<boolean>(false);

  //set carry forwarded annual leaves dropdown values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dropDownValueCarryForwardedLeaves = [
    { item: 0, value: 0 },
    { item: 1, value: 1 },
    { item: 2, value: 2 },
    { item: 3, value: 3 },
    { item: 4, value: 4 },
    { item: 5, value: 5 },
    { item: 6, value: 6 },
    { item: 7, value: 7 },
    { item: 8, value: 8 },
    { item: 9, value: 9 },
    { item: 10, value: 10 },
    { item: 11, value: 11 },
    { item: 12, value: 12 },
    { item: 13, value: 13 },
    { item: 14, value: 14 }
  ];

  const containsText = (text, searchText) =>
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const displayedCarryForwardedLeaves = useMemo(
    () =>
      dropDownValueCarryForwardedLeaves?.filter((option) =>
        containsText(option.value.toString(), searchText)
      ),
    [searchText, dropDownValueCarryForwardedLeaves]
  );

  useEffect(() => {
    setSelectedCarryForwardedLeaves('');
    setSelectedExpireDate('');
    setSearchText('');
  }, [props.open]);

  const onSubmit = () => {
    const modifyCarryForwardedLeaves = props.carryForwardedLeaveList
      .filter((_, index) => props.selectedIndexes.includes(index))
      .map((item) => ({
        ...item,
        carryForwardLeaves: parseInt(selectedCarryForwardLeaves ? selectedCarryForwardLeaves : item.carryForwardLeaves, 10),
        expiredDate: selectedExpiredDate ? selectedExpiredDate : item.expiredDate
      }));

    dispatch(AppActions.showLoader());
    bulkUpdateCarryForwardedLeaves(modifyCarryForwardedLeaves)
      .then((res) => {
        props.snackBarMessage(
          'Carry forward leave has bulk updated successfully.'
        );
        props.snackbarSeverity('success');
        props.setSnackBarOpen(true);
        setOpenConformation(!openConformation);
        props.toggle();
        dispatch(AppActions.hideLoader());
      })
      .catch((e) => {
        dispatch(AppActions.hideLoader());
        props.snackBarMessage(
          'Unable to bulk update carry forwarded annual leave.'
        );
        props.snackbarSeverity('error');
        props.setSnackBarOpen(true);
        setOpenConformation(!openConformation);
        props.toggle();
      });
  };

  const validateConformation = () => {
    setOpenConformation(!openConformation);
  }

  const noToggleConformation = () => {
    setOpenConformation(!openConformation);
  };

  return (
    <Dialog
      open={props.open}
      keepMounted
      maxWidth="sm"
      fullWidth
      scroll="paper"
      onClose={props.toggle}
    >
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          padding: '2rem',
          height: '15rem'
        }}
      >
        <Grid sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Bulk Update Carry Forwarded Annual Leaves
          </Typography>
        </Grid>
        <Grid
          xs={12}
          sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
        >
          <Grid item xs={6}>
            <InputLabel
              style={{
                fontSize: '12px',
                marginBottom: '0.5rem'
              }}
            >
              Carry Forwarded Leaves
            </InputLabel>
            <FormControl fullWidth>
              <Select
                id="selectedProject"
                MenuProps={{ autoFocus: false }}
                style={{
                  fontSize: '12px',
                  height: '2.3rem'
                }}
                size="small"
                inputProps={{ 'aria-label': 'Without label' }}
                displayEmpty
                defaultValue=""
                value={selectedCarryForwardLeaves}
                onChange={(e) => {
                  setSelectedCarryForwardedLeaves(e.target.value);
                }}
                onClose={() => setSearchText('')}
              >
                <ListSubheader>
                  <TextField
                    id="searchSelectedProject"
                    size="small"
                    autoFocus
                    placeholder="Type to search..."
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Escape') {
                        e.stopPropagation();
                      }
                    }}
                  ></TextField>
                </ListSubheader>
                <MenuItem
                  disabled
                  selected
                  style={{ display: 'none' }}
                  value={''}
                >
                  Select Carry Forwarded Leaves
                </MenuItem>
                {displayedCarryForwardedLeaves.map((element, i) => {
                  return (
                    <MenuItem value={element.value} key={i}>
                      {element.item}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <InputLabel
              style={{
                fontSize: '12px',
                marginBottom: '0.5rem'
              }}
            >
              Expired Date
            </InputLabel>
            <FormControl fullWidth>
              <DatePicker
                InputProps={{
                  className: 'date-picker'
                }}
                renderInput={(params) => (
                  <TextField {...params} id="selectedToDate" />
                )}
                value={selectedExpiredDate ? selectedExpiredDate : null}
                onChange={(newValue) => {
                  const expireDate = moment(newValue);
                  setSelectedExpireDate(expireDate.format('YYYY-MM-DD'));
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem',
            alignItems: 'center'
          }}
        >
          <Button
            sx={{
              width: '7rem',
              fontWeight: 'normal',
              background: '#F5F7FF',
              fontSize: '13px'
            }}
            type="button"
            onClick={() => props.toggle()}
          >
            Cancel
          </Button>
          <Button
            disabled={(selectedCarryForwardLeaves === '' && !selectedExpiredDate) ? true : false}
            sx={{
              width: '7rem',
              fontSize: '13px',
              fontWeight: 'normal',
              background: '#0069E4'
            }}
            variant="contained"
            type="button"
            onClick={() => validateConformation()}
          >
            Update
          </Button>
        </Grid>
      </Grid>
      <ConfirmationModal
        isLoading={isLoading}
        open={openConformation}
        toggle={validateConformation}
        message={
          'Are you sure you want to bulk update carry forward leaves?'
        }
        onConfirm={() => {
          onSubmit();
        }}
        noToggle={noToggleConformation}
      />
    </Dialog>
  );
};

export default BulkUpdate;
